// =============================================================================
// features/simulator/SimulatorForm.tsx — Formulário de simulação (F2-S06).
//
// Layout: sticky na coluna esquerda (desktop). Mobile: empilha acima do resultado.
// Campos: lead (combobox), produto (select), valor (input numérico), prazo (meses).
// Validação live contra a regra ativa do produto selecionado.
// Submete SimulationBody para useSimulate (POST /api/simulations).
// DS: Input §9.2, Button §9.1, Card elev-2 para o wrapper.
//
// CONTRATO (F2-S11):
//   Input de valor é type="number" — digitar 30000 = R$ 30.000 (sem digit-shift).
//   onSubmit emite camelCase: {leadId, productId, amount, termMonths}.
// =============================================================================

import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../components/ui/Button';
import { CurrencyInput } from '../../components/ui/CurrencyInput';
import { Input } from '../../components/ui/Input';
import type { LeadResponse } from '../../hooks/crm/types';
import type {
  CreditProduct,
  SimulationBody,
  SimulatorFormValues,
} from '../../hooks/simulator/types';
import { formatBRL } from '../../hooks/simulator/types';
import type { SimulationError } from '../../hooks/simulator/useSimulate';
import { cn } from '../../lib/cn';

import { defaultFirstDueDate, minFirstDueDate, maxFirstDueDate } from './firstDueDate';
import { LeadCombobox } from './LeadCombobox';
import { ProductSelect } from './ProductSelect';

// ─── Validação Zod ────────────────────────────────────────────────────────────

/**
 * Schema de validação dinâmico — limites vêm da regra ativa do produto.
 * Criado via factory para aceitar os limites como parâmetro.
 * amount é validado como number (input type="number" com valueAsNumber).
 */
function buildSchema(rule: CreditProduct['active_rule']) {
  return z.object({
    leadId: z.string().min(1, 'Selecione um lead'),
    productId: z.string().min(1, 'Selecione um produto'),
    amount: z
      .number({ invalid_type_error: 'Informe o valor solicitado' })
      .positive('Valor deve ser positivo')
      .refine(
        (v) => {
          if (!rule) return true;
          return v >= rule.min_amount && v <= rule.max_amount;
        },
        {
          message: rule
            ? `Valor deve estar entre ${formatBRL(rule.min_amount)} e ${formatBRL(rule.max_amount)}`
            : 'Valor inválido',
        },
      ),
    termMonths: z
      .string()
      .min(1, 'Informe o prazo')
      .refine(
        (v) => {
          const n = parseInt(v, 10);
          if (!rule) return !isNaN(n) && n > 0;
          return !isNaN(n) && n >= rule.min_term_months && n <= rule.max_term_months;
        },
        {
          message: rule
            ? `Prazo deve estar entre ${rule.min_term_months} e ${rule.max_term_months} meses`
            : 'Prazo inválido',
        },
      ),
    // Formato apenas — o mínimo de 30 dias já é imposto pelo `min` nativo do
    // seletor (defaultFirstDueDate/minFirstDueDate); o teto e a validação de
    // negócio de verdade são do backend (FIRST_DUE_DATE_TOO_SOON/_TOO_LATE).
    firstDueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data da primeira parcela'),
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SimulatorFormProps {
  isPending: boolean;
  simulationError: SimulationError | null;
  /** Emite body pronto para POST /api/simulations — camelCase, alinhado ao backend. */
  onSubmit: (values: SimulationBody) => void;
  onLeadChange?: (lead: LeadResponse | null) => void;
  /**
   * Lead pré-selecionado, vindo de `?leadId=` na URL (deep link do CRM).
   * O `LeadCombobox` resolve o nome sozinho a partir do id.
   */
  initialLeadId?: string | undefined;
}

// ─── Componente ──────────────────────────────────────────────────────────────

/**
 * Formulário de simulação de crédito.
 * Sticky no desktop, empilhado no mobile.
 */
export function SimulatorForm({
  isPending,
  simulationError,
  onSubmit,
  onLeadChange,
  initialLeadId,
}: SimulatorFormProps): React.JSX.Element {
  const [selectedProduct, setSelectedProduct] = React.useState<CreditProduct | null>(null);

  const schema = React.useMemo(
    () => buildSchema(selectedProduct?.active_rule ?? null),
    [selectedProduct],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    trigger,
  } = useForm<SimulatorFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      // Deep link do CRM (`/simulator?leadId=…`) já entra com o lead escolhido:
      // o operador não repete a busca que acabou de fazer na tela anterior.
      leadId: initialLeadId ?? '',
      productId: '',
      // amount starts as NaN so the field is empty; Zod rejects NaN (not positive)
      amount: NaN,
      termMonths: '',
      // Default = hoje + 30 dias (carência mínima do BdP) — mesma condição
      // padrão do backend quando o campo é omitido.
      firstDueDate: defaultFirstDueDate(),
    },
  });

  const watchedLeadId = watch('leadId');
  const watchedProductId = watch('productId');
  const watchedAmount = watch('amount');

  // Re-valida campos de valor e prazo quando produto muda (limites mudam)
  React.useEffect(() => {
    if (watchedProductId) {
      void trigger(['amount', 'termMonths']);
    }
  }, [selectedProduct, trigger, watchedProductId]);

  function handleLeadChange(leadId: string, lead: LeadResponse | null) {
    setValue('leadId', leadId, { shouldValidate: true });
    onLeadChange?.(lead);
  }

  function handleProductChange(productId: string, product: CreditProduct | null) {
    setValue('productId', productId, { shouldValidate: true });
    setSelectedProduct(product);
  }

  function handleFormSubmit(values: SimulatorFormValues) {
    onSubmit({
      leadId: values.leadId,
      productId: values.productId,
      amount: values.amount,
      termMonths: parseInt(values.termMonths, 10),
      firstDueDate: values.firstDueDate,
    });
  }

  const rule = selectedProduct?.active_rule ?? null;

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-surface-1 p-5 flex flex-col gap-5',
        // Sticky no desktop
        'lg:sticky lg:top-5',
      )}
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      {/* Header */}
      <div>
        <h2
          className="font-display font-bold text-ink leading-tight"
          style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.028em' }}
        >
          Simular crédito
        </h2>
        <p className="font-sans text-xs text-ink-3 mt-1">
          Preencha os dados para calcular parcela e amortização.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        className="flex flex-col gap-4"
        aria-label="Formulário de simulação de crédito"
      >
        {/* Lead */}
        <LeadCombobox
          value={watchedLeadId}
          onChange={handleLeadChange}
          error={errors.leadId?.message}
          disabled={isPending}
        />

        {/* Produto */}
        <ProductSelect
          value={watchedProductId}
          onChange={handleProductChange}
          error={errors.productId?.message}
          disabled={isPending}
        />

        {/* Valor — CurrencyInput canônico em REAIS (F18-S03) */}
        <CurrencyInput
          id="amount-input"
          label="Valor solicitado (R$)"
          required
          disabled={isPending}
          value={Number.isNaN(watchedAmount) ? null : watchedAmount}
          onChange={(reais) =>
            setValue('amount', reais === null ? NaN : reais, { shouldValidate: true })
          }
          error={errors.amount?.message}
          hint={
            rule
              ? `Faixa: ${formatBRL(rule.min_amount)} – ${formatBRL(rule.max_amount)}`
              : undefined
          }
        />

        {/* Prazo */}
        <Input
          id="term-months-input"
          label="Prazo (meses)"
          type="number"
          min={rule?.min_term_months ?? 1}
          max={rule?.max_term_months ?? 360}
          disabled={isPending}
          error={errors.termMonths?.message}
          hint={rule ? `De ${rule.min_term_months} a ${rule.max_term_months} meses` : undefined}
          placeholder={rule ? `${rule.min_term_months}–${rule.max_term_months}` : 'Ex: 24'}
          required
          {...register('termMonths')}
        />

        {/* Data da primeira parcela — F44-S06.
            Default e mínimo = hoje + 30 dias (carência mínima do BdP), impostos
            no próprio seletor. O teto exibido é só UX; quem valida de verdade é
            o backend (FIRST_DUE_DATE_TOO_SOON/_TOO_LATE), e o erro do servidor
            aparece aqui — no campo — nunca num toast genérico. */}
        <Input
          id="first-due-date-input"
          label="Data da primeira parcela"
          type="date"
          min={minFirstDueDate()}
          max={maxFirstDueDate()}
          disabled={isPending}
          error={
            errors.firstDueDate?.message ??
            (simulationError?.field === 'firstDueDate' ? simulationError.message : undefined)
          }
          hint="Mínimo de 30 dias de carência a partir de hoje."
          required
          {...register('firstDueDate')}
        />

        {/* Erros de rede / API */}
        {simulationError && simulationError.code === 'UNKNOWN' && (
          <div
            role="alert"
            className="rounded-sm border-l-[3px] p-3 text-sm font-sans"
            style={{
              borderColor: 'var(--danger)',
              background: 'var(--danger-bg)',
              color: 'var(--danger)',
            }}
          >
            <strong className="font-semibold">Erro ao simular:</strong> {simulationError.message}
          </div>
        )}

        {/* Botão submit */}
        <Button
          type="submit"
          variant="primary"
          size="default"
          disabled={isPending || !isValid}
          className="w-full mt-1"
          leftIcon={
            isPending ? (
              <span
                className="block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"
                aria-hidden="true"
              />
            ) : (
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="w-4 h-4"
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            )
          }
        >
          {isPending ? 'Calculando…' : 'Simular'}
        </Button>
      </form>
    </div>
  );
}
